import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { Box, Paper } from "@mui/material";
import MessageList from "./chat/MessageList";
import ChatInput from "./chat/ChatInput";
import WelcomeScreen from "./WelcomeScreen";
// import SuggestionBubbles from "./SuggestionBubble";
import "../App.css";

import { runQuery, getInsights } from "../services/api";
import { getFileInfo } from "../utils/storage";

const ChatArea = forwardRef(function ChatArea(
  {
    rows,
    schema,
    onMessage,
    onSchemaUpdate,
    onFileParsed,
    darkMode,
    togglePreview,
    isPreviewOpen,
    isFileUploaded,
    messages: messagesProp,
  },
  ref
) {
  const [messages, setMessages] = useState([]);
  const [chatStage, setChatStage] = useState("welcome");
  const [isTyping, setIsTyping] = useState(false);

  const [showWelcome, setShowWelcome] = useState(
    !sessionStorage.getItem("welcomeShown")
  );

  useImperativeHandle(ref, () => ({
    resetChat: () => {
      setMessages([
        {
          role: "assistant",
          text: "Hi — upload a file using the + button, then ask me to show a table, visualize a column, or query the data.",
        },
      ]);
      setChatStage("welcome");
    },
    onIncoming: (msg) => {
      setMessages((prev) => {
        if (
          prev.length > 0 &&
          prev[prev.length - 1].role === msg.role &&
          prev[prev.length - 1].text === msg.text
        ) {
          return prev;
        }
        return [...prev, msg];
      });
    },
    hideWelcome: () => {
      setShowWelcome(false);
      sessionStorage.setItem("welcomeShown", "true");
    },
  }));

  useEffect(() => {
    if (messagesProp && messagesProp.length > 0) {
      setMessages(messagesProp);
    } else {
      setMessages([
        {
          role: "assistant",
          text: "Hi — upload a file using the + button, then ask me to show a table, visualize a column, or query the data.",
        },
      ]);
    }
  }, [messagesProp]);

  const pushMessage = (msg) => {
    setMessages((prev) => [...prev, msg]);
    onMessage?.(msg);
  };

  const delayedAssistantReply = (msg) => {
    setIsTyping(true);
    setTimeout(() => {
      pushMessage({ role: "assistant", ...msg });
      setIsTyping(false);
    }, 0);
  };

  // Helper function to format success data into a table-like string
  const formatSuccessData = (successArray) => {
    if (!Array.isArray(successArray) || successArray.length === 0) {
      return "No data available.";
    }

    // If there is only one object with a single key, display it concisely
    if (successArray.length === 1) {
      const obj = successArray[0];
      const keys = Object.keys(obj);
      if (keys.length === 1) {
        return `${keys[0]}: ${obj[keys[0]]}`;
      }
    }

    // Create table-like output for multiple rows or columns
    const keys = Object.keys(successArray[0]);
    const header = keys.join(" | ");
    const separator = keys.map(() => "---").join(" | ");
    const rows = successArray
      .map((row) => keys.map((k) => row[k]).join(" | "))
      .join("\n");

    return `${header}\n${separator}\n${rows}`;
  };

  const handleSend = async (text) => {
    const cleanText = text.trim();
    pushMessage({ role: "user", text });

    setIsTyping(true);

    try {
      const meta = await getFileInfo();
      if (!meta || !meta.fileName) {
        pushMessage({
          role: "assistant",
          text: "⚠️ No file metadata found. Please re-upload your file.",
        });
        setIsTyping(false);
        return;
      }

      const fileNameWithoutExt = meta.fileName.replace(/\.[^/.]+$/, "").trim();

      // 1️⃣ Run the query
      const result = await runQuery(
        fileNameWithoutExt,
        meta.userId,
        meta.sessionId,
        text
      );

      if (!result?.success) {
        pushMessage({
          role: "assistant",
          text: "⚠️ I didn’t get a proper response. Try asking differently.",
        });
        setIsTyping(false);
        return;
      }

      // 2️⃣ Get insights using the runQuery response
      const insights = await getInsights(result);

      // 3️⃣ Prepare the message metadata
      const metaForMessage = {
        preface: result.preface,
        title: result.Title || "Data Response",
        xAxis: result["X-axis"],
        yAxis: result["Y-axis"],
        charts: result.Possible_charts,
        data: result.success,
        insights: insights || "No insights available.", // ✅ store insights
      };

      pushMessage({
        role: "assistant",
        text: `${metaForMessage.title}`,
        meta: metaForMessage,
      });
    } catch (err) {
      pushMessage({
        role: "assistant",
        text: "⚠️ Sorry, something went wrong while fetching the data.",
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleUpload = async (rows, filename, file) => {
    // 1️⃣ Hide the welcome screen immediately
    hideWelcome();

    // // 2️⃣ Clear previous messages and show uploading message
    // setMessages([
    //   {
    //     role: "assistant",
    //     text: `📂 Uploading ${filename}... Please wait...`,
    //   },
    // ]);

    // 3️⃣ Move chat stage forward
    setChatStage("fileUploaded");

    // 4️⃣ Fire the file parsing logic (now awaited)
    await onFileParsed?.(rows, filename, file);

    // 5️⃣ After parsing completes, add a success message
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        text: `✅ ${filename} uploading....! `,
      },
    ]);
  };
  // const handleUpload = (rows, filename, file) => {
  //   onFileParsed?.(rows, filename, file);
  //   setChatStage("fileUploaded");
  //   delayedAssistantReply({
  //     text: `Uploading ${filename} 📂.... Please wait...`,
  //   });
  // };

  const handleSchemaUpdate = (newSchema) => {
    onSchemaUpdate?.(newSchema);
    setChatStage("schemaConfirmed");
  };

  const hideWelcome = () => {
    setShowWelcome(false);
    sessionStorage.setItem("welcomeShown", "true");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Paper
        elevation={8}
        sx={{
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
          background: darkMode
            ? "linear-gradient(145deg, #1e293b, #111827)"
            : "linear-gradient(145deg, #f0f4f8, #e2e8f0)",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            scrollbarWidth: "thin", // for Firefox
            scrollbarColor: darkMode ? "#64748b #1e293b" : "#cbd5e1 #f0f4f8", // thumb and track
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: darkMode ? "#1e293b" : "#f0f4f8",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: darkMode ? "#64748b" : "#cbd5e1",
              borderRadius: "3px",
            },
          }}
        >
          {showWelcome ? (
            <WelcomeScreen onStart={hideWelcome} darkMode={darkMode} />
          ) : (
            <>
              <MessageList
                messages={messages}
                onSchemaUpdate={handleSchemaUpdate}
                darkMode={darkMode}
              />
              {isTyping && (
                <Box sx={{ p: 2, color: "gray", fontStyle: "italic" }}>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span>Thinking</span>
                </Box>
              )}
            </>
          )}
        </Box>

        <ChatInput
          onSend={(text) => {
            hideWelcome();
            handleSend(text);
          }}
          onUpload={(rows, filename, file) => {
            hideWelcome();
            handleUpload(rows, filename, file);
          }}
          onFocus={hideWelcome}
          togglePreview={() => {
            hideWelcome();
            togglePreview();
          }}
          isPreviewOpen={isPreviewOpen}
          isFileUploaded={isFileUploaded}
          rows={rows}
          chatStage={chatStage}
        />
      </Paper>
    </Box>
  );
});

export default ChatArea;
